import { isUndefined } from 'lodash'
import React, { useContext, useMemo } from 'react'
import { useDynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { SystemSettingHashes } from 'src/Libs/navigation/types'
import { TimeInMilliseconds } from 'src/Libs/types'
import { isFlowTheme } from 'src/Libs/utils'
import useActiveChargingSessionStatus from '../../hooks/use-active-charging-session-status'
import { MappedWallboxForWidgets } from '../../hooks/use-data-for-wallbox-widgets'
import { VehicleWidget } from '../vehicle-integration-widget'
import WallboxWidget from './WallboxWidget'
import { useFarmContext } from 'src/Elements/Farm'
import { VehicleWidgetData } from '../../types'

/**
 * @deprecated use Libs/wallbox/models/wallbox-configuration.model.ts instead
 * TODO: refactor usage
 */
export interface WallboxConfiguration {
  index: number
  deviceName: string
  deviceState: {
    deviceConnected: boolean
    deviceWorking: boolean
    deviceInService: boolean
  }
  chargeFull: boolean
  pmPowerL1: number
  pmPowerL2: number
  pmPowerL3: number
  pmEnergyL1: number
  pmEnergyL2: number
  pmEnergyL3: number
  minPowerPerPhase: number
  maxPowerPerPhase: number
  soc: number
  status: number
  sunModeActive: boolean
  numberPhases: 1 | 3
  maxChargeCurrent: number
  minChargeCurrent: number
  upperCurrentLimit: number
  wallboxType: number
  lowerCurrentLimit: number
  chargeStopHysteresis: number
  activeChargeStrategy: 'now' | 'delayed' | 'deactivated'
  keyState: number
  appSoftware: number
  serial: string
  rfidReaderEnabled: boolean
  authRequired: boolean
  maxChargeCurrentWatingFor: boolean
  sunmode: boolean
  chargingCanceled: boolean
  chargingActive: boolean
  oneload: boolean
  schuko: boolean
  wallboxCurrentOverview: {
    SUN: number
    NET: number
    carConnected: boolean
  }
  parameterList: {
    daytime: number
    minEnergy: number
  }[]
  dhcpEnabled: boolean
  ipAddr: string
  subnetMask: string
  gateway: string
  keyLockMode: 'lock' | 'sun'
}

interface Props {
  title?: string
  wallboxData: MappedWallboxForWidgets
  id: string
  installationId?: string
  storageId?: string
  isLoading?: boolean
  vehicle?: VehicleWidgetData
}

/**
 * Data Card for wallbox used to be displayed on the dashboard
 *
 * TODO: to introduce sun mode look in git history. Relevant ticket is CPE-1876.
 */
const WallboxWidgetContainer: React.FC<Props> = ({ title, id, wallboxData, installationId, storageId, isLoading, vehicle }) => {
  const {
    isAvailable,
    chargingData,
    number: wallboxIndex,
    storageSerialNumber: wallboxStorageId,
    currentPowerPrice,
    hasSmartComponent,
    sunMode,
  } = wallboxData
  const isSunModeActive = Boolean(sunMode)
  const inServerView = storageId !== wallboxStorageId.toString()
  const { isStorageOnline, offlineClientStorageIds, isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm, isFarmMasterSelected } = useFarmContext()

  const isUnavailable = !isLoading && !isAvailable
  const isOffline =
    isFarm && isFarmMasterSelected
      ? !isStorageOnline || offlineClientStorageIds?.includes(wallboxStorageId.toString()) || isUnavailable
      : !isStorageOnline || isUnavailable

  const { isChargingSessionPaused, toggleWallboxChargingStatus } = useActiveChargingSessionStatus({
    wallboxIndex,
    storageId,
    enabled: !isUndefined(chargingData), // we only need to fetch if there is an active charging session, hence if socket data is defined
    refetchInterval: TimeInMilliseconds.tenSecond,
  })

  const link = useMemo(() => {
    if (isFlowTheme) {
      return `/installation/${installationId}/${storageId}/properties/wallbox/${wallboxIndex}`
    } else {
      if (inServerView) {
        return `/installation/${installationId}/${storageId}/properties/wallbox/${wallboxStorageId}/${wallboxIndex}/${SystemSettingHashes.WallboxSettings}`
      }

      return `/installation/${installationId}/${wallboxStorageId}/properties/wallbox/${wallboxIndex}/${SystemSettingHashes.WallboxSettings}`
    }
  }, [installationId, wallboxStorageId, storageId, wallboxIndex, inServerView])

  const { isDynamicPowerPriceEnabledForWallboxes } = useDynamicPowerPricesEnabledContext()
  const isServerOffline = isFarm && isServerOnline !== true

  return (
    <>
      {vehicle ? (
        <VehicleWidget
          isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
          isChargingSessionPaused={isChargingSessionPaused}
          isServerOffline={isServerOffline}
          isOffline={isOffline}
          toggleWallboxChargingStatus={toggleWallboxChargingStatus}
          currentPowerPrice={currentPowerPrice}
          wallboxType={wallboxData.wallboxType}
          chargingStatus={chargingData?.chargingStatus}
          energyAll={chargingData?.energyAll}
          vehicle={vehicle}
          isSunModeActive={isSunModeActive}
          isBoostModeActive={wallboxData.isBoostModeActive}
          hasSmartComponent={hasSmartComponent}
          wallboxIndex={wallboxIndex}
          wallboxStorageId={wallboxStorageId}
        />
      ) : (
        <WallboxWidget
          id={id}
          isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
          link={link}
          isChargingSessionPaused={isChargingSessionPaused}
          isServerOffline={isServerOffline}
          isOffline={isOffline}
          toggleWallboxChargingStatus={toggleWallboxChargingStatus}
          currentPowerPrice={currentPowerPrice}
          title={title}
          wallboxType={wallboxData.wallboxType}
          chargingStatus={chargingData?.chargingStatus}
          energyAll={chargingData?.energyAll}
          energyDischarged={chargingData?.energyDischarged}
          isSunModeActive={isSunModeActive}
          isBoostModeActive={wallboxData.isBoostModeActive}
          hasSmartComponent={hasSmartComponent}
          wallboxIndex={wallboxIndex}
          wallboxStorageId={wallboxStorageId}
        />
      )}
    </>
  )
}

export default WallboxWidgetContainer
